import { useCallback } from "react";
import { useEditState } from "../components/Editor/EditContext";
import { useMapData } from "../components/Editor/Map/MapDataContext";
import { useRestAuth } from "./useRestAuth";
import { EditorData } from "../utils/editorUtils";

/**
 * Custom hook that provides a function to refresh all editor layer data.
 * This hook encapsulates the common pattern of refreshing map data after edits.
 * 
 * @param setRefreshingData - Optional callback to set loading state
 * @returns Function to refresh layer data
 */
export const useRefreshEditorData = (
  setRefreshingData?: (loading: boolean) => void
) => {
  const { mapPanoRepository, mapPoiRepository, target, editData } = useEditState();
  const { updateLayerData, markAllLayersStale } = useMapData();
  const restAuth = useRestAuth();

  const refreshLayerData = useCallback(async () => {
    if (!restAuth) return;

    setRefreshingData?.(true);

    try {
      if (target) {
        // For single layer data: fetch that layer's data again
        const panoLinks = await mapPanoRepository.get(restAuth, target);
        const poiLinks = await mapPoiRepository.get(restAuth, target);
        const unlinkedPanoLinks = await mapPanoRepository.get_unlinked(restAuth, target);
        const unlinkedPoiLinks = await mapPoiRepository.get_unlinked(restAuth, target);
        const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
        const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);

        const refreshedData: EditorData<"map"> = {
          panorama_location: panoLinks,
          poi_location: poiLinks,
          unlinked_panorama_location: unlinkedPanoLinks,
          unlinked_poi_location: unlinkedPoiLinks,
          unused_panorama_location: unusedPanoLinks,
          unused_poi_location: unusedPoiLinks,
        };

        // Update cache with fresh data
        updateLayerData(target, refreshedData);
        editData<"map">(refreshedData);
      } else {
        // For global data: fetch all global data again and clear layer cache
        const panoLinks = await mapPanoRepository.get(restAuth);
        const poiLinks = await mapPoiRepository.get(restAuth);
        const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
        const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);

        const refreshedData: EditorData<"map"> = {
          panorama_location: panoLinks,
          poi_location: poiLinks,
          unlinked_panorama_location: [],
          unlinked_poi_location: [],
          unused_panorama_location: unusedPanoLinks,
          unused_poi_location: unusedPoiLinks,
        };

        // Clear cache and force reload all layers since global changes affect all layers
        markAllLayersStale();
        editData<"map">(refreshedData);
      }
    } finally {
      setRefreshingData?.(false);
    }
  }, [
    restAuth,
    target,
    mapPanoRepository,
    mapPoiRepository,
    updateLayerData,
    editData,
    markAllLayersStale,
    setRefreshingData,
  ]);

  return refreshLayerData;
};